import { useEffect, useMemo, useState } from 'react';
import api from '../../lib/apiClient';
import Pagination from '../../components/Pagination';
import usePaginatedList from '../../hooks/usePaginatedList';

interface AuditLogRow {
  id: number;
  log_name: string;
  event: 'created' | 'updated' | 'deleted';
  description: string;
  subject_type: string | null;
  subject_id: number | null;
  causer_name: string | null;
  changes: { attributes?: Record<string, unknown>; old?: Record<string, unknown> } | null;
  created_at: string;
}

const EVENT_BADGE: Record<AuditLogRow['event'], string> = {
  created: 'badge-active',
  updated: 'badge-neutral',
  deleted: 'badge-important',
};

function formatValue(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (Array.isArray(v)) return v.length ? v.join(', ') : '—';
  return String(v);
}

function ChangeList({ changes }: { changes: AuditLogRow['changes'] }) {
  const keys = Object.keys(changes?.attributes ?? {});
  if (keys.length === 0) return <span className="muted">—</span>;

  return (
    <ul className="simple-list">
      {keys.map((key) => (
        <li key={key}>
          <strong>{key}</strong>: {formatValue(changes?.old?.[key])} → {formatValue(changes?.attributes?.[key])}
        </li>
      ))}
    </ul>
  );
}

export function AuditLog() {
  const [logName, setLogName] = useState('');
  const [event, setEvent] = useState('');
  const [logNames, setLogNames] = useState<string[]>([]);

  useEffect(() => {
    api.get<string[]>('/audit-log/log-names').then((res) => setLogNames(res.data));
  }, []);

  const filters = useMemo(() => ({ log_name: logName || undefined, event: event || undefined }), [logName, event]);
  const { data, loading, error, setPage } = usePaginatedList<AuditLogRow>('/audit-log', filters);

  return (
    <>
      <header className="page-header">
        <h1>Audit Log</h1>
      </header>
      <p className="muted">
        Every logged change to an audited record — who changed it, when, and the old and new values. Currently
        covers assets, the chart of accounts, correcting journal entries, investments, user accounts (including
        role changes) and employee records.
      </p>

      <div className="toolbar">
        <select value={logName} onChange={(e) => setLogName(e.target.value)}>
          <option value="">All logs</option>
          {logNames.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
        <select value={event} onChange={(e) => setEvent(e.target.value)}>
          <option value="">All events</option>
          <option value="created">Created</option>
          <option value="updated">Updated</option>
          <option value="deleted">Deleted</option>
        </select>
      </div>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>When</th>
                <th>Log</th>
                <th>Event</th>
                <th>Subject</th>
                <th>Changed By</th>
                <th>Changes</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="muted center">
                    Loading…
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={6} className="error center">
                    {error}
                  </td>
                </tr>
              ) : data && data.data.length > 0 ? (
                data.data.map((row) => (
                  <tr key={row.id}>
                    <td className="muted">{new Date(row.created_at).toLocaleString()}</td>
                    <td>{row.log_name}</td>
                    <td>
                      <span className={`badge ${EVENT_BADGE[row.event]}`}>{row.event}</span>
                    </td>
                    <td className="muted">
                      {row.subject_type} #{row.subject_id}
                    </td>
                    <td>{row.causer_name ?? '—'}</td>
                    <td>
                      <ChangeList changes={row.changes} />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="muted center">
                    No audit log entries yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination meta={data} onPage={setPage} />
      </div>
    </>
  );
}
